import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import CoachCard from "../../../../Components/CoachCard";
import { useGetCoachesQuery } from "../../../../Store/api/queries";

export default function CoachesContent({ queryParams }) {
  const [page, setPage] = useState(1);
  const [allCoaches, setAllCoaches] = useState([]);
  const observerRef = useRef(null);

  // ادغام پارامترهای فیلتر با صفحه جاری
  const queryArgs = useMemo(() => ({
    ...queryParams,
    page,
    limit: 10,
  }), [queryParams, page]);

  const { data, isLoading, isFetching, isError } = useGetCoachesQuery(queryArgs);

  // استخراج لیست مربیان و اطلاعات صفحه‌بندی متناسب با فرمت vanta-api
  const coachesList = data?.data?.docs || data?.data || [];
  const totalCount = data?.count || data?.totalDocs || 0;
  const totalPages = Math.ceil(totalCount / 10) || 1;
  const hasMore = page < totalPages;

  // با تغییر فیلترها (به جز تغییر صفحه)، کل استیت‌ها ریست شوند تا لیست جدید نمایش داده شود
  useEffect(() => {
    setPage(1);
    setAllCoaches([]);
  }, [queryParams]);

  // مرج کردن داده‌های جدید با داده‌های قبلی بدون ایجاد آیتم تکراری
  useEffect(() => {
    if (!coachesList.length) {
      if (page === 1) setAllCoaches([]);
      return;
    }

    setAllCoaches((prev) => {
      if (page === 1) return coachesList;
      const existingIds = new Set(prev.map((item) => item._id));
      const filteredNew = coachesList.filter((item) => !existingIds.has(item._id));
      return [...prev, ...filteredNew];
    });
  }, [coachesList, page]);

  // تعریف Intersection Observer جهت شناسایی انتهای لیست برای بارگذاری صفحه بعد
  const lastCoachRef = useCallback(
    (node) => {
      if (isFetching) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prev) => prev + 1);
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [isFetching, hasMore]
  );

  // پاکسازی آبزرور در زمان خارج شدن از کامپوننت
  useEffect(() => {
    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, []);

  // لودینگ اولیه صفحه اول
  if (isLoading && page === 1) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  // نمایش پیام خطا
  if (isError && page === 1) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography color="error" variant="h6">
          خطا در دریافت اطلاعات مربیان. لطفا دوباره تلاش کنید.
        </Typography>
      </Box>
    );
  }

  // نمایش پیام عدم وجود دیتا
  if (allCoaches.length === 0 && !isFetching) {
    return (
      <Box
        sx={{
          p: 5,
          textAlign: "center",
          bgcolor: "background.paper",
          borderRadius: 4,
        }}
      >
        <Typography color="text.secondary" variant="h6">
          مربی با این مشخصات یافت نشد!
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            lg: "repeat(2, 1fr)",
            xxl: "repeat(3, 1fr)",
          },
          gap: { xs: 2, sm: 2.5, md: 3 },
          pb: "1rem",
        }}
      >
        {allCoaches.map((coach, index) => {
          // متصل کردن رفرنس به آخرین آیتم موجود در لیست فعلی
          if (index === allCoaches.length - 1) {
            return (
              <div ref={lastCoachRef} key={coach._id}>
                <CoachCard trainer={coach} />
              </div>
            );
          }
          return <CoachCard key={coach._id} trainer={coach} />;
        })}
      </Box>

      {/* نمایش لودینگ کوچک فقط در صورت بارگذاری صفحات بعدی در پس‌زمینه */}
      {isFetching && page > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress color="primary" size={32} />
        </Box>
      )}
    </Box>
  );
}
